import { useEffect, useRef } from "react";

// Simple line icons, drawn on a 24×24 grid.
const PATHS = {
  plane: (
    <>
      <path d="M2.5 13.5 21 6.5l-4.5 13-4-5.5-5.5 2 1-4.5z" />
      <path d="m12.5 14 8.5-7.5" />
    </>
  ),
  parachute: (
    <>
      <path d="M3 10a9 7 0 0 1 18 0" />
      <path d="M3 10c1.5-1.2 3-1.2 4.5 0 1.5-1.2 3-1.2 4.5 0 1.5-1.2 3-1.2 4.5 0 1.5-1.2 3-1.2 4.5 0" />
      <path d="M3 10l8 8M21 10l-8 8M12 10v8" />
      <rect x="10.5" y="18" width="3" height="3.5" rx="1" />
    </>
  ),
  waves: (
    <>
      <path d="M2 8c2-1.5 3.5-1.5 5 0s3 1.5 5 0 3.5-1.5 5 0 3 1.5 5 0" />
      <path d="M2 13c2-1.5 3.5-1.5 5 0s3 1.5 5 0 3.5-1.5 5 0 3 1.5 5 0" />
      <path d="M2 18c2-1.5 3.5-1.5 5 0s3 1.5 5 0 3.5-1.5 5 0 3 1.5 5 0" />
    </>
  ),
  camera: (
    <>
      <path d="M4 7.5h3l1.5-2.5h7L17 7.5h3a1.5 1.5 0 0 1 1.5 1.5v9A1.5 1.5 0 0 1 20 19.5H4A1.5 1.5 0 0 1 2.5 18V9A1.5 1.5 0 0 1 4 7.5z" />
      <circle cx="12" cy="13" r="3.5" />
    </>
  ),
  compass: (
    <>
      <circle cx="12" cy="12" r="9.5" />
      <path d="m15.5 8.5-2 5-5 2 2-5z" />
    </>
  ),
  laptop: (
    <>
      <rect x="4" y="4.5" width="16" height="11" rx="1.5" />
      <path d="M2 19.5h20" />
      <path d="m10 8.5-2 1.5 2 1.5M14 8.5l2 1.5-2 1.5" />
    </>
  ),
};

// Physics tuning (pixels and seconds).
const GRAVITY = 1800; // px/s²: sets how quickly each pendulum swings back
const DAMPING = 0.9; // per second: how fast swinging dies down
const PUSH_RADIUS = 26; // px around an icon that the cursor can knock
const PUSH_STRENGTH = 0.6;
const MAX_ANGLE = 1.35; // rad, ~77°
const MAX_SPIN = 7; // rad/s
const RAIL_Y = 22;
const BOB = 40; // icon tile size in px
const LENGTHS = [0.62, 0.42, 0.72, 0.5, 0.66, 0.38]; // string length as a share of the drop height

// A row of pendulums on a rail. Brush past an icon to knock it, or grab and fling it.
const HangingIcons = ({ icons }) => {
  const boxRef = useRef(null);
  const stringRefs = useRef([]);
  const glowRefs = useRef([]);
  const pivotRefs = useRef([]);
  const bobRefs = useRef([]);

  useEffect(() => {
    const box = boxRef.current;
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const bobs = icons.map((_, i) => ({
      angle: reduceMotion ? 0 : (i % 2 ? 0.12 : -0.12), // small starting swing
      spin: 0,
      pivotX: 0,
      length: 0,
      dragging: false,
      lastAngle: 0,
    }));
    const pointer = { x: -999, y: -999, vx: 0, vy: 0, t: 0, inside: false };
    let frame = null;
    let last = performance.now();
    let visible = true;

    const layout = () => {
      const { width, height } = box.getBoundingClientRect();
      const pad = width * 0.1;
      const drop = height - RAIL_Y - BOB / 2 - 14;
      bobs.forEach((b, i) => {
        b.pivotX = pad + ((width - pad * 2) * i) / Math.max(1, icons.length - 1);
        b.length = drop * LENGTHS[i % LENGTHS.length] + 30;
      });
    };

    const bobPos = (b) => ({
      x: b.pivotX + Math.sin(b.angle) * b.length,
      y: RAIL_Y + Math.cos(b.angle) * b.length,
    });

    const render = () => {
      bobs.forEach((b, i) => {
        const { x, y } = bobPos(b);
        const s = stringRefs.current[i];
        const g = glowRefs.current[i];
        for (const line of [s, g]) {
          if (!line) continue;
          line.setAttribute("x1", b.pivotX);
          line.setAttribute("y1", RAIL_Y);
          line.setAttribute("x2", x);
          line.setAttribute("y2", y - BOB / 2 + 2);
        }
        pivotRefs.current[i]?.setAttribute("cx", b.pivotX);
        const el = bobRefs.current[i];
        if (el) el.style.transform = `translate(${x - BOB / 2}px, ${y - BOB / 2}px) rotate(${(-b.angle * 180) / Math.PI}deg)`;
      });
    };

    const step = (now) => {
      const dt = Math.min(0.032, (now - last) / 1000);
      last = now;

      bobs.forEach((b) => {
        if (b.dragging) return;
        // Cursor brushing past gives the icon a shove along its swing direction.
        if (pointer.inside && now - pointer.t < 60) {
          const { x, y } = bobPos(b);
          const d = Math.hypot(pointer.x - x, pointer.y - y);
          if (d < PUSH_RADIUS + BOB / 2) {
            const tangential = pointer.vx * Math.cos(b.angle) - pointer.vy * Math.sin(b.angle);
            b.spin += ((tangential / b.length) * PUSH_STRENGTH * (1 - d / (PUSH_RADIUS + BOB / 2)));
          }
        }
        const accel = -(GRAVITY / b.length) * Math.sin(b.angle) - DAMPING * b.spin;
        b.spin = Math.max(-MAX_SPIN, Math.min(MAX_SPIN, b.spin + accel * dt));
        b.angle = Math.max(-MAX_ANGLE, Math.min(MAX_ANGLE, b.angle + b.spin * dt));
        if (Math.abs(b.angle) === MAX_ANGLE) b.spin *= -0.4; // soft bounce at the limit
      });

      render();
      frame = visible ? requestAnimationFrame(step) : null;
    };

    const local = (e) => {
      const r = box.getBoundingClientRect();
      return { x: e.clientX - r.left, y: e.clientY - r.top };
    };

    const onMove = (e) => {
      const { x, y } = local(e);
      const now = performance.now();
      const dt = Math.max(1, now - pointer.t) / 1000;
      if (pointer.inside) {
        // Smooth the velocity so a single jittery sample doesn't launch an icon.
        pointer.vx = pointer.vx * 0.5 + ((x - pointer.x) / dt) * 0.5;
        pointer.vy = pointer.vy * 0.5 + ((y - pointer.y) / dt) * 0.5;
      }
      pointer.x = x;
      pointer.y = y;
      pointer.t = now;
      pointer.inside = true;

      bobs.forEach((b) => {
        if (!b.dragging) return;
        const target = Math.atan2(x - b.pivotX, Math.max(8, y - RAIL_Y));
        const clamped = Math.max(-MAX_ANGLE, Math.min(MAX_ANGLE, target));
        b.spin = (clamped - b.lastAngle) / dt;
        b.lastAngle = b.angle = clamped;
      });
    };
    const onLeave = () => {
      pointer.inside = false;
      pointer.vx = pointer.vy = 0;
    };
    const release = () => {
      bobs.forEach((b) => {
        if (!b.dragging) return;
        b.dragging = false;
        b.spin = Math.max(-MAX_SPIN, Math.min(MAX_SPIN, b.spin));
        bobRefs.current[bobs.indexOf(b)]?.classList.remove("is-grabbed");
      });
    };
    const grabHandlers = bobs.map((b, i) => (e) => {
      e.preventDefault();
      b.dragging = true;
      b.lastAngle = b.angle;
      b.spin = 0;
      bobRefs.current[i]?.classList.add("is-grabbed");
    });

    layout();
    render();
    const ro = new ResizeObserver(() => {
      layout();
      render();
    });
    ro.observe(box);

    if (reduceMotion) return () => ro.disconnect();

    const bobEls = [...bobRefs.current];
    bobEls.forEach((el, i) => el?.addEventListener("pointerdown", grabHandlers[i]));
    box.addEventListener("pointermove", onMove);
    box.addEventListener("pointerleave", onLeave);
    const onWindowMove = (e) => bobs.some((b) => b.dragging) && onMove(e);
    window.addEventListener("pointermove", onWindowMove);
    window.addEventListener("pointerup", release);

    const io = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      if (visible && frame === null) {
        last = performance.now();
        frame = requestAnimationFrame(step);
      }
    });
    io.observe(box);
    frame = requestAnimationFrame(step);

    return () => {
      if (frame !== null) cancelAnimationFrame(frame);
      ro.disconnect();
      io.disconnect();
      bobEls.forEach((el, i) => el?.removeEventListener("pointerdown", grabHandlers[i]));
      box.removeEventListener("pointermove", onMove);
      box.removeEventListener("pointerleave", onLeave);
      window.removeEventListener("pointermove", onWindowMove);
      window.removeEventListener("pointerup", release);
    };
  }, [icons]);

  return (
    <div
      ref={boxRef}
      className="relative w-full max-w-[340px] h-[260px] mx-auto rounded-3xl bg-card border border-ink/10 bg-dots overflow-hidden touch-none select-none"
      aria-label="Travel, skydiving, ocean, photography, exploring and tech"
      role="img"
    >
      <svg className="absolute inset-0 w-full h-full pointer-events-none" aria-hidden="true">
        <line x1="4%" x2="96%" y1={RAIL_Y} y2={RAIL_Y} className="stroke-ink/25" strokeWidth="1.5" />
        {icons.map((name, i) => (
          <g key={name}>
            <line ref={(el) => (stringRefs.current[i] = el)} className="stroke-ink/35" strokeWidth="1" />
            {/* a small pink pulse that runs down each string */}
            <line
              ref={(el) => (glowRefs.current[i] = el)}
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeDasharray="10 140"
              className="stroke-accent animate-string-pulse"
              style={{ animationDelay: `${i * 0.45}s` }}
            />
            <circle ref={(el) => (pivotRefs.current[i] = el)} cy={RAIL_Y} r="3.5" className="fill-ink/45" />
          </g>
        ))}
      </svg>
      {icons.map((name, i) => (
        <div
          key={name}
          ref={(el) => (bobRefs.current[i] = el)}
          className="hanging-bob absolute left-0 top-0 rounded-xl bg-primary border border-ink/10 shadow-[0_8px_18px_rgb(33_29_27/0.14)] flex items-center justify-center text-ink cursor-grab will-change-transform"
          style={{ width: BOB, height: BOB }}
        >
          <svg
            viewBox="0 0 24 24"
            className="w-5 h-5 pointer-events-none"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            {PATHS[name]}
          </svg>
        </div>
      ))}
    </div>
  );
};

export default HangingIcons;
